import React, { memo, useEffect, useMemo } from "react";

// Vendor
import SearchIcon from "@mui/icons-material/Search";
import { Col, DatePicker, Form, Row, TreeSelect, Typography } from "antd";
import moment from "moment";

// Utils
import {
    smoothnessTooltipFormatter,
    smoothnessYAxisLabelFormatterHandler,
    tooltipFormatter,
    tooltipTitle,
    yAxisLabelFormatterHandler,
} from "@Components/activity/helpers";
import SessionManager from "@Core/session";
import { filterTreeNode, getActivityMetric, ITreeItem } from "@Utils";

// Models
import { IActivityDetails } from "@Models/IActivityDetails";
import { IPointsMetadataRequest } from "@Models/IPoints";

// Components
import CertificationOverview from "@Components/activity//CertificationOverview";
import AreaChart from "@Components/activity/AreaChart";
import ColumnGraph from "@Components/activity/ColumnGraph";
import useActivitySearch from "@Components/activity/hooks/useActivitySearch";
import useAssociatedStatuses from "@Components/activity/hooks/useAssociatedStatuses";
import HorizontalBar from "@Components/activity/HorizontalBar";
import MetricsPercentileGraph from "@Components/activity/MetricsPercentileGraph";
import {
    IActivitySearch,
    IProgressionDistanceChartValue,
    IProgressionDurationChartValue,
    IProgressionSmoothnessChartValue,
    ISearchTerms,
} from "@Components/activity/types";
import IconButton from "@Components/shared/IconButton";
import LargeSpinner from "@Components/shared/LargeSpinner";
import { ILoadNextPage } from "@Components/shared/types";
import HandednessGraph from "@Components/user/HandednessGraph";
import ActivitySearchList from "@Components/activity/ActivitySearchList";

// Styles
import "@Styles/main.scss";

const { Title } = Typography;

interface IProps {
    activities: IActivityDetails[];
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    isSearching: boolean;
    terms: ISearchTerms;
    userId: string;
    activityCategoriesTree: ITreeItem[];

    loadNextPage: ILoadNextPage;
    handleActivitySelect: (userId: string, activityInstanceId: string) => void;
    handleSearchSelect: (values: IActivitySearch) => Promise<void>;
}

const ActivitySearch: React.FC<IProps> = ({
    activities,
    hasNextPage,
    isNextPageLoading,
    isSearching,
    terms,
    userId,
    activityCategoriesTree,

    loadNextPage,
    handleActivitySelect,
    handleSearchSelect,
}: IProps) => {
    const [form] = Form.useForm();
    const { RangePicker } = DatePicker;
    const { userId: currentUserId } = SessionManager.getServiceUser();
    const isOwner = userId === currentUserId;

    const activityInstanceIds = activities?.map((a) => a.activityInstanceId) ?? [];

    const { associatedStatusesMap, isHydrated, progress } = useAssociatedStatuses({
        activities,
        loggedInUser: currentUserId,
    });

    const { dateTerms = [] } = terms;
    const [startDate, endDate] = dateTerms;

    const metadataRequest: IPointsMetadataRequest = {
        activityInstanceIds,
        endDate,
        ownerId: userId,
        startDate,
    };

    const onFinish = async (values: IActivitySearch) => {
        await handleSearchSelect(values);
    };

    useEffect(() => {
        // the form should clear when the user changes
        form.resetFields();
    }, [form, userId]);

    const summaryValues = useMemo(() => {
        /* summaryValues.countedHandednessValues: use for count "getActivityMetric" values bigger than "0" ! */
        const summaryValues = {
            duration: 0,
            distance: 0,
            distanceGlobal: 0,
            durationMedian: 0,
            handedness: 0,
            countedHandednessValues: 0,
            speed: 0,
            precision: 0,
            smoothness: 0,
        };
        activities?.forEach((activity, index, array) => {
            if (activity !== undefined && activity !== null && activity?.activityId !== null) {
                const handedness = getActivityMetric("handedness", activity);
                const speed = getActivityMetric("duration-percentile", activity);
                const precision = getActivityMetric("precision", activity);
                const smoothness = getActivityMetric("smoothness", activity);
                const distance = getActivityMetric("distance", activity);
                const distanceGlobal = getActivityMetric("distance-global", activity);
                summaryValues.duration += activity.duration;
                summaryValues.durationMedian += activity.durationMedian;
                summaryValues.speed += speed;
                summaryValues.precision += precision;
                summaryValues.smoothness += smoothness;
                summaryValues.distance += distance;
                summaryValues.distanceGlobal += distanceGlobal;

                if (handedness !== 0) {
                    summaryValues.handedness += handedness;
                    summaryValues.countedHandednessValues += 1;
                }
                if (index === array.length - 1) {
                    summaryValues.duration /= array.length;
                    summaryValues.durationMedian /= array.length;
                    summaryValues.handedness /= summaryValues.countedHandednessValues;
                    summaryValues.speed /= array.length;
                    summaryValues.precision /= array.length;
                    summaryValues.smoothness /= array.length;
                    summaryValues.distance /= array.length;
                    summaryValues.distanceGlobal /= array.length;
                }
            }
        });
        return summaryValues;
    }, [activities]);

    let progressionDurationData: IProgressionDurationChartValue[] = [];
    let progressionSmoothnessData: IProgressionSmoothnessChartValue[] = [];
    let progressionDistanceData: IProgressionDistanceChartValue[] = [];

    const renderResults = activities !== undefined && activities !== null && activities?.length > 0;

    if (activities !== undefined && activities !== null && activities[0] !== undefined) {
        progressionDurationData = activities
            ?.map((x) => ({
                Date: x?.creationDate,
                Duration: getActivityMetric("duration", x),
                ActivityInstanceId: x.activityInstanceId,
            }))
            .sort((a, b) => {
                return new Date(a.Date).getTime() - new Date(b.Date).getTime();
            });

        const maxSmoothnessValue = Math.max(...activities?.map((item) => getActivityMetric("smoothness", item)));
        progressionSmoothnessData = activities
            ?.map((x) => ({
                Date: x?.creationDate,
                // Create percentage value for Smoothness
                Smoothness: (getActivityMetric("smoothness", x) / maxSmoothnessValue) * 100,
                ActivityInstanceId: x.activityInstanceId,
            }))
            .sort((a, b) => {
                return new Date(a.Date).getTime() - new Date(b.Date).getTime();
            });

        progressionDistanceData = activities
            ?.map((x) => ({
                Date: x?.creationDate,
                Distance: getActivityMetric("distance", x),
                ActivityInstanceId: x?.activityInstanceId,
            }))
            .sort((a, b) => {
                return new Date(a.Date).getTime() - new Date(b.Date).getTime();
            });
    }

    const showProgressionDurationChart = terms.activityTerms.length === 1 && progressionDurationData.length > 1;
    const showProgressionSmoothnessChart =
        terms.activityTerms.length === 1 &&
        progressionSmoothnessData.length > 1 &&
        progressionSmoothnessData
            .map((x) => x.Smoothness)
            .reduce((x, y) => {
                return x + y;
            }, 0) > 0;

    const numberOfSummaries = [summaryValues.precision, summaryValues.smoothness, summaryValues.distance].filter(
        (s) => s > 0
    ).length;
    const mdWidth = numberOfSummaries > 2 ? 8 : 12;

    const { distanceGraphOptions } = useActivitySearch({
        distance: summaryValues.distance,
        distanceGlobal: summaryValues.distanceGlobal,
    });

    return (
        activityCategoriesTree?.length > 0 && (
            <section className="activity-search">
                <div className="activity-search-form">
                    <Form
                        form={form}
                        name="activity-search"
                        onFinish={async (values: IActivitySearch) => {
                            await onFinish(values);
                        }}
                        autoComplete="off"
                    >
                        <Row>
                            <Col
                                md={12}
                                xs={24}
                                style={{
                                    display: "flex",
                                    paddingRight: "8px",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                }}
                            >
                                <Form.Item name="activityTerms" className="activity-select">
                                    <TreeSelect
                                        disabled={isSearching}
                                        placeholder="Select or type..."
                                        treeData={activityCategoriesTree}
                                        treeCheckable={true}
                                        filterTreeNode={filterTreeNode}
                                    />
                                </Form.Item>
                                <Form.Item name="dateTerms" required>
                                    <RangePicker
                                        defaultPickerValue={[moment().subtract(1, "month"), moment()]}
                                        format={"MMM Do YYYY"}
                                        disabled={isSearching}
                                        allowClear
                                    />
                                </Form.Item>
                                <Form.Item name="dateTerms" className="activity-search-button" required>
                                    <IconButton
                                        name="submit"
                                        icon={<SearchIcon style={{ fontSize: "14px" }} />}
                                        position="right"
                                        className="dark"
                                        htmlType="submit"
                                        loading={isSearching}
                                    >
                                        Search
                                    </IconButton>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </div>
                {isSearching && <LargeSpinner height={600} />}
                {!isSearching && (
                    <>
                        <Row gutter={[16, 8]} className="activity-search-row">
                            <Col md={12} xs={24}>
                                <ActivitySearchList
                                    renderResults={renderResults}
                                    activities={activities}
                                    associatedStatusesMap={associatedStatusesMap}
                                    hasNextPage={hasNextPage}
                                    isNextPageLoading={isNextPageLoading}
                                    loadNextPage={loadNextPage}
                                    handleActivitySelect={handleActivitySelect}
                                />
                            </Col>
                            <Col md={12} xs={24}>
                                {renderResults && (
                                    <HorizontalBar
                                        values={[summaryValues.duration, summaryValues.durationMedian]}
                                        labels={["User duration", "Global average"]}
                                        timeFormat={true}
                                    />
                                )}
                            </Col>
                        </Row>
                        {isOwner && renderResults ? (
                            <CertificationOverview
                                activities={activities}
                                associatedStatusesMap={isHydrated ? associatedStatusesMap : undefined}
                                metadataRequest={metadataRequest}
                                progress={progress}
                                terms={terms}
                            />
                        ) : null}
                        {renderResults &&
                        (summaryValues.precision > 0 || summaryValues.smoothness || summaryValues.handedness) ? (
                            <div>
                                <Row>
                                    <Title level={3} style={{ width: "100%" }}>
                                        Metrics
                                    </Title>
                                    {(summaryValues.precision > 0 || summaryValues.smoothness) &&
                                    summaryValues.handedness <= 0 ? (
                                        <span style={{ marginBottom: "30px", display: "block" }}>
                                            Smoothness, speed and precision
                                        </span>
                                    ) : (
                                        <span style={{ marginBottom: "30px", display: "block" }}>
                                            Smoothness, speed, precision and handedness
                                        </span>
                                    )}
                                </Row>
                                <Row>
                                    {activities?.length === 1 ? (
                                        <Col md={mdWidth} xs={24}>
                                            <MetricsPercentileGraph activity={activities[0]} />
                                        </Col>
                                    ) : (
                                        <Col md={mdWidth} xs={24}>
                                            {(summaryValues.precision > 0 || summaryValues.smoothness > 0) && (
                                                <MetricsPercentileGraph
                                                    entries={[
                                                        { type: "Speed", value: summaryValues.speed },
                                                        { type: "Precision", value: summaryValues.precision },
                                                        { type: "Smoothness", value: summaryValues.smoothness },
                                                    ]}
                                                />
                                            )}
                                        </Col>
                                    )}
                                    <Col md={mdWidth} xs={24}>
                                        {summaryValues.handedness > 0 && (
                                            <HandednessGraph
                                                percentage={[summaryValues.handedness, 100 - summaryValues.handedness]}
                                            />
                                        )}
                                    </Col>
                                    <Col md={mdWidth} xs={24}>
                                        {summaryValues.distance > 0 && <ColumnGraph {...distanceGraphOptions} />}
                                    </Col>
                                </Row>
                            </div>
                        ) : null}
                        {showProgressionDurationChart && renderResults && (
                            <Row>
                                <Col md={24} xs={24}>
                                    <Title level={3} style={{ width: "100%" }}>
                                        Progression (Completion Time)
                                    </Title>
                                    <span style={{ marginBottom: "30px", display: "block" }}>
                                        Completion times over successive attempts for this activity
                                    </span>
                                    <AreaChart<IProgressionDurationChartValue>
                                        handleActivitySelect={handleActivitySelect}
                                        userId={userId}
                                        values={progressionDurationData}
                                        xField="Date"
                                        yField="Duration"
                                        xAxisLabelFormatter={(val): string => moment(val).format("MMM Do YYYY")}
                                        yAxisLabelFormatter={yAxisLabelFormatterHandler}
                                        tooltipFormatter={tooltipFormatter}
                                        tooltipTitle={tooltipTitle}
                                        title=""
                                    />
                                </Col>
                            </Row>
                        )}
                        {showProgressionSmoothnessChart && renderResults && (
                            <Row>
                                <Col md={24} xs={24}>
                                    <Title level={3} style={{ width: "100%" }}>
                                        Progression (Smoothness)
                                    </Title>
                                    <span style={{ marginBottom: "30px", display: "block" }}>
                                        Smoothness over successive attempts for this activity
                                    </span>
                                    <AreaChart<IProgressionSmoothnessChartValue>
                                        handleActivitySelect={handleActivitySelect}
                                        userId={userId}
                                        values={progressionSmoothnessData}
                                        xField="Date"
                                        yField="Smoothness"
                                        xAxisLabelFormatter={(val): string => moment(val).format("MMM Do YYYY")}
                                        yAxisLabelFormatter={smoothnessYAxisLabelFormatterHandler}
                                        tooltipFormatter={smoothnessTooltipFormatter}
                                        tooltipTitle={tooltipTitle}
                                        title=""
                                    />
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </section>
        )
    );
};

export default memo(ActivitySearch);
